import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {formatAnnualValidationMessage} from '@/utils/glImportHelpers';
import {
    Box, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, CircularProgress, Typography,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTheme} from '@mui/material/styles';
import {
    CompanyAnnual, type CompanyAnnualPayload,
    getAnnual, updateAnnual, createAnnual
} from '@/services/accountService/companyAnnualService';
import {SnackbarField} from '@/components/common';
import EpPeriod from '@/pages/components/company-info/company-annual/sub-content/EpPeriod';
import EpDividend from '@/pages/components/company-info/company-annual/sub-content/EpDividend';
import EpBankConfirmations from '@/pages/components/company-info/company-annual/sub-content/EpBankConfirmations';
import EpCompanyDetails from '@/pages/components/company-info/company-annual/sub-content/EpCompanyDetails';
import EpAuditOpinion from '@/pages/components/company-info/company-annual/sub-content/EpAuditOpinion';
import EpOtherDisclosures from '@/pages/components/company-info/company-annual/sub-content/EpOtherDisclosures';
import EpHoldingStructure from '@/pages/components/company-info/company-annual/sub-content/EpHoldingStructure';
import EpAllotmentOfShares from '@/pages/components/company-info/company-annual/sub-content/EpAllotmentOfShares';
import EpShareholding from '@/pages/components/company-info/company-annual/sub-content/EpShareholding';
import EpCompanyNameAuditorChanges from '@/pages/components/company-info/company-annual/sub-content/EpCompanyNameAuditorChanges';
import EpBusiness from '@/pages/components/company-info/company-annual/sub-content/EpBusiness';
import { sanitizeFormerAuditorAddress } from '@/utils/formerAuditorFields';
import EpCurrencyExchange from '@/pages/components/company-info/company-annual/sub-content/EpCurrencyExchange';
import EpSmallCashConfirmation from '@/pages/components/company-info/company-annual/sub-content/EpSmallCashConfirmation';
import EpTax from '@/pages/components/company-info/company-annual/sub-content/EpTax';
import EpAuditStaffs from '@/pages/components/company-info/company-annual/sub-content/EpAuditStaffs';
import EpAuditor from '@/pages/components/company-info/company-annual/sub-content/EpAuditor';
import EpOther from '@/pages/components/company-info/company-annual/sub-content/EpOther';
import FirstAuditPeriodConfirmDialog from '@/pages/components/company-info/company-annual/FirstAuditPeriodConfirmDialog';
import {useFirstAuditPeriodConfirm} from '@/pages/components/company-info/company-annual/useFirstAuditPeriodConfirm';
import {useCompanyAnnualStore} from "@/store/audit/companyAnnualStore";
import {AuditFsAnnualService} from '@/services/auditService';
import { validateCompanyAnnualFormCreateFlow, type CompanyAnnualCreateFlow } from '@/utils/companyAnnualFlowGuard';
import { invalidateFsAdjustmentCachesForAnnual } from '@/utils/fsAdjustmentRefresh';
import { useQueryClient } from '@tanstack/react-query';
import { TaxRepService } from '@/services/manageService/taxRepService';
import { isCompleteTaxRepPhone, normalizeTaxRepPhone } from '@/utils/taxRepPhone';
import { validateTaxRepBr } from '@/components/common';
import { DATE_FORMAT_HINT, isStrictDdMmYyyy, normalizeToDdMmYyyy } from '@/utils/dataUtils';

export interface CompanyAnnualDetailHandle {
    submit: () => Promise<boolean>;
    getValidationMessage: () => string | null;
}

interface Props {
    open: boolean;
    onClose: (payload?: { message?: string; severity?: 'success' | 'error' }) => void;
    annualId?: number;
    isAdd?: boolean;
    // When provided, fixes the company selection to this ID (used by FsCreateStepper)
    companyId?: number;
    lockCompanySelection?: boolean;
    inline?: boolean;
    // Explicit rendering mode similar to CompanyDetail
    renderMode?: 'dialog' | 'content';
    createGuardFlow?: CompanyAnnualCreateFlow;
    createFlowType?: 1 | 2;
    initialData?: Partial<Record<string, any>>;
    // When true, hides the internal Create/Save/Cancel action buttons (for external trigger via ref.submit)
    hideActions?: boolean;
}

const CompanyAnnualDetail = forwardRef<CompanyAnnualDetailHandle, Props>(function CompanyAnnualDetail({
                                                  open,
                                                  onClose,
                                                  annualId,
                                                  isAdd,
                                                  companyId,
                                                  lockCompanySelection,
                                                  inline,
                                                  renderMode,
                                                  createGuardFlow,
                                                  createFlowType,
                                                  initialData,
                                                  hideActions,
                                              }, ref) {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const mode: 'dialog' | 'content' = (renderMode ? renderMode : (inline ? 'content' : 'dialog'));
    const isActive = open || inline || mode === 'content';
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState<CompanyAnnual | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error'
    }>({open: false});
    // Required field validation errors
    const [validationErrors, setValidationErrors] = useState<{
        periodFrom?: string;
        periodTo?: string;
        taxFileNo?: string;
        taxRep?: string;
    }>({});
    const lastValidationMessageRef = useRef<string | null>(null);
    const periodConfirmValidateRef = useRef<(() => boolean) | null>(null);

    const scrollToAnnualField = (field: 'taxFileNo' | 'period' | 'taxRep') => {
        requestAnimationFrame(() => {
            document.getElementById(`annual-detail-field-${field}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        });
    };
    // Store inCorpDate from CompanyDetailsExpansionPanel
    const [inCorpDate, setInCorpDate] = useState<string | null>(null);

    // Auto-detect first audit: periodFrom === inCorpDate
    const isFirstAuditForced = useMemo(() => {
        if (!inCorpDate || !form?.periodFrom) return false;
        return inCorpDate === form.periodFrom;
    }, [inCorpDate, form?.periodFrom]);

    // Auto-set firstAudit = 1 when periodFrom === inCorpDate
    useEffect(() => {
        if (isFirstAuditForced && form && form.firstAudit !== 1) {
            setForm(prev => prev ? ({...prev, firstAudit: 1}) as CompanyAnnual : prev);
        }
    }, [isFirstAuditForced, form?.firstAudit]);

    const applyFirstAuditConfirmPatch = useCallback((patch: {
        firstAuditFollows18Months?: boolean | null;
        firstAuditYearEndMD?: string | null;
    }) => {
        setForm(prev => prev ? ({...prev, ...patch} as CompanyAnnual) : prev);
        const id = Number(annualId || form?.annualId || 0);
        if (id > 0) {
            const existing = useCompanyAnnualStore.getState().byAnnualId[id];
            if (existing) {
                useCompanyAnnualStore.getState().setAnnual({...existing, ...patch} as CompanyAnnual);
            }
        }
    }, [annualId, form?.annualId]);

    const {
        dialogOpen: firstAuditDialogOpen,
        handleConfirm: handleFirstAuditConfirm,
        isBlocking: isFirstAuditBlocking,
        blockingMessage: firstAuditBlockingMessage,
    } = useFirstAuditPeriodConfirm({
        periodFrom: form?.periodFrom,
        periodTo: form?.periodTo,
        inCorpDate,
        cessationDuringThisPeriod: form?.cessationDuringThisPeriod,
        firstAuditYearEndMD: form?.firstAuditYearEndMD,
        onPatch: applyFirstAuditConfirmPatch,
    });

    /** Excel 导入的期间字段 — 稳定引用，避免 EpPeriod useEffect 循环 */
    const initialPeriodData = useMemo(() => {
        if (!initialData) return undefined;
        const hasPeriod = initialData.periodFrom || initialData.periodTo
            || initialData.lastYearPeriodFrom || initialData.lastYearPeriodTo
            || initialData.cessationDuringThisPeriod != null;
        if (!hasPeriod) return undefined;
        return {
            periodFrom: initialData.periodFrom ?? null,
            periodTo: initialData.periodTo ?? null,
            lastYearPeriodFrom: initialData.lastYearPeriodFrom ?? null,
            lastYearPeriodTo: initialData.lastYearPeriodTo ?? null,
            cessationDuringThisPeriod: initialData.cessationDuringThisPeriod,
            isMultiYearAudit: initialData.isMultiYearAudit ?? true,
        };
    }, [
        initialData?.periodFrom,
        initialData?.periodTo,
        initialData?.lastYearPeriodFrom,
        initialData?.lastYearPeriodTo,
        initialData?.cessationDuringThisPeriod,
        initialData?.isMultiYearAudit,
    ]);

    useEffect(() => {
        if (!isActive) return;
        setError(null);
        setSnackbar({open: false});
        if (isAdd) {
            // Initialize empty form for creation
            const empty: CompanyAnnual = {
                annualId: 0,
                companyId: companyId || 0,
                companyName: '',
                periodFrom: null,
                periodTo: null,
                lastYearPeriodFrom: null,
                lastYearPeriodTo: null,
                yearOfAssessment: null,
                taxFileNo: null,
                limitByGuarantee: 0,
                liabilitiesCurrency: '',
                liabilitiesAmount:0,
                otherNameThanCompany: null,
                section88: 0,
                bankConfirmations: [],
                qualifyOpinion: 0,
                firstAudit: 0,
                firstAuditFollows18Months: null,
                firstAuditYearEndMD: null,
                isMultiYearAudit: true,
                formerAuditor: '',
                addressOfFormerAuditor: '',
                professionalClearance: null,
                auditCertificateId: 0,
                auditStaffs: [],
                registeredOffice: null,
                currentRegisteredOffice: null,
                directors: [],
                shareholders: [],
                monthsInPeriod: 0,
                daysInPeriod: 0,
                auditMonths: 0,
                isChangeName: 0,
                offShoreClaim: 0,
                changeEffectiveDate: null,
                oldCompanyName: null,
                oldCompanyChineseName: null,
                currentCompanyName: null,
                currentCompanyChineseName: null,
                companyChineseName: null,
                changeOfFinancialYearEnd: 0,
                dateOfBoardResolution: null,
                reasonToChange: null,
                cessationDuringThisPeriod: 0,
                presentationCurrency: 'HKD',
                exchangeRate: 1,
                functionalCurrency: 'HKD',
                dividendPaid: 0,
                dividendCurrency: null,
                amountPerEachShare: 0,
                totalDividendAmount: 0,
                twoTierTaxThisYear: 1,
                twoTierTaxLastYear: 1,
                assessableResultType: 0,
                assessableAmount: 0,
                taxLossBF: 0,
                taxLossCF: 0,
                changeOfAddress: 0,
                lastAddress: null,
                changeDate: null,
                ppe: 0,
                relatedPartyTransactions: 0,
                rentalCommitment: 0,
                holdingCo: 0,
                nameOfImmediate: null,
                immediateHolding: null,
                nameOfUltimate: null,
                ultimateHolding: null,
                allotmentOfShares: 0,
                allotmentAmount: 0,
                noOfShares: 0,
                pricePerShare: 0,
                priceCurrency: null,
                dividendPaidOfOverseas: 0,
                overseasDividendCurrency: null,
                overseasDividendAmountPerShare: 0,
                overseasTotalDividendAmount: 0,
                principalActivities: 2,
                businessNature: null,
                commenceWithinPeriod: 0,
                commenceDate: null,
                netLiabilities: 0,
                pettyCashConfirmation: '',
                pettyCashAmount: 0,
                irdLetterSigner: '',
                taxRepSameAsAuditor: 1,
                taxRepName: null,
                taxRepAddress: null,
                taxRepTel: null,
                taxRepBrNumber: null,
                taxRepCertificateId: null,
                taxRepId: null,
                taxRepHerebyAuthorize: null,
                referrer: null,
                inCharge: null,
            };
            // Apply initialData overrides if provided
            if (initialData) {
                Object.entries(initialData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && key in empty) {
                        (empty as any)[key] = value;
                    }
                });
            }
            setForm(empty);
            return;
        }
        if (!annualId) {
            setForm(null);
            return;
        }
        (async () => {
            setLoading(true);
            try {
                // Prefer getAnnual so API failures surface as error (loadAnnual swallows catch → No data)
                const data = await getAnnual(annualId);
                if (data && typeof data.annualId !== 'undefined') {
                    // section88 empty → 0 for form/store consistency
                    data.section88 = Number(data.section88) === 1 ? 1 : 0;
                    useCompanyAnnualStore.getState().setAnnual(data);
                }
                // Apply initialData overrides in edit mode (for Excel import preview/update)
                if (initialData && data) {
                    Object.entries(initialData).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && key in data) {
                            (data as any)[key] = value;
                        }
                    });
                }
                setForm(data ?? null);
                if (!data) {
                    setError(`Annual detail not found (id=${annualId})`);
                }
            } catch (e: any) {
                setForm(null);
                setError(e?.message || 'Failed to load annual detail');
            } finally {
                setLoading(false);
            }
        })();
    }, [isActive, annualId, isAdd, initialData]);

    const onSave = async (): Promise<boolean> => {
        if (!form || !annualId) return false;

        const normalized = normalizeFormDates(form);
        if (!normalized) {
            setSnackbar({
                open: true,
                message: DATE_FORMAT_HINT,
                severity: 'error',
            });
            scrollToAnnualField('period');
            validateRequiredFields(form);
            return false;
        }
        if (normalized !== form) {
            setForm(normalized);
        }

        // Validate required fields
        if (!validateRequiredFields(normalized)) {
            const message = lastValidationMessageRef.current || 'Please fill in all required fields';
            setSnackbar({
                open: true,
                message,
                severity: 'error'
            });
            return false;
        }

        if (periodConfirmValidateRef.current && !periodConfirmValidateRef.current()) {
            scrollToAnnualField('period');
            return false;
        }

        if (isFirstAuditBlocking) {
            setSnackbar({
                open: true,
                message: firstAuditBlockingMessage,
                severity: 'error',
            });
            return false;
        }

        if (Number(normalized.isChangeName) === 1) {
            const hasNewEn = Boolean((normalized.companyName || '').trim());
            const hasNewZh = Boolean((normalized.companyChineseName || '').trim());
            if (!hasNewEn && !hasNewZh) {
                setSnackbar({
                    open: true,
                    message: 'Please enter the new Company Name or Company Chinese Name.',
                    severity: 'error',
                });
                return false;
            }
            if (!(normalized.changeEffectiveDate || '').trim()) {
                setSnackbar({
                    open: true,
                    message: 'Please fill in "Since when?" (change effective date) when Change of Co. name is Yes.',
                    severity: 'error',
                });
                return false;
            }
        }

        try {
            setLoading(true);
            let working = normalized;
            try {
                working = await resolveTaxRepUpsert(normalized);
                if (working !== normalized) {
                    setForm(working);
                }
            } catch (e: any) {
                setSnackbar({open: true, message: e?.message || 'Failed to save Tax Rep', severity: 'error'});
                return false;
            }
            const seen = new Set<number>();
            const cleanAuditStaffs = ((working.auditStaffs || []))
                .filter(s => s && Number(s.userId || 0) > 0)
                .filter(s => {
                    const id = Number(s.userId);
                    if (seen.has(id)) return false;
                    seen.add(id);
                    return true;
                }).map(s => ({userId: Number(s.userId), roleType: Number(s.roleType || 0)}));
            const isChangeNameYes = Number(working.isChangeName) === 1;
            const payload: CompanyAnnualPayload = {
                ...working,
                functionalCurrency: working.functionalCurrency ?? 'HKD',
                liabilitiesCurrency: working.liabilitiesCurrency ?? 'HKD',
                section88: Number(working.section88) === 1 ? 1 : 0,
                isChangeName: isChangeNameYes ? 1 : Number(working.isChangeName) === 0 ? 0 : working.isChangeName,
                ...(isChangeNameYes
                    ? {
                        // Ensure old names reach backend even if toggle missed currentCompany*
                        oldCompanyName: (working.oldCompanyName || '').trim()
                            || (working.currentCompanyName || '').trim()
                            || null,
                        oldCompanyChineseName: (working.oldCompanyChineseName || '').trim()
                            || (working.currentCompanyChineseName || '').trim()
                            || null,
                        changeEffectiveDate: (working.changeEffectiveDate || '').trim() || null,
                    }
                    : Number(working.isChangeName) === 0
                        ? {
                            changeEffectiveDate: null,
                            oldCompanyName: null,
                            oldCompanyChineseName: null,
                        }
                        : {}),
                auditStaffs: cleanAuditStaffs
            };
            const res = await updateAnnual(annualId, payload);
            setForm(res);
            // Tax auto adj 等可能已变：失效同 companyId/annualId 的 FsAdjustment 缓存
            const resolvedCompanyId = Number(res?.companyId || working.companyId || companyId || 0);
            const resolvedAnnualId = Number(res?.annualId || annualId || 0);
            if (resolvedCompanyId && resolvedAnnualId) {
                try {
                    useCompanyAnnualStore.getState().setAnnual(res);
                } catch {
                }
                invalidateFsAdjustmentCachesForAnnual(resolvedCompanyId, resolvedAnnualId, queryClient);
            }
            onClose({message: 'Update Successful!', severity: 'success'});
            return true;
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Update failed!', severity: 'error'});
            return false;
        } finally {
            setLoading(false);
        }
    };

    /** Manual Tax Rep (freeSolo): all detail fields required. List selection skips this. */
    const isManualTaxRep = (f: CompanyAnnual) =>
        Number(f.taxRepSameAsAuditor) === 0
        && !f.taxRepCertificateId
        && Boolean((f.taxRepName || '').trim());

    const validateTaxRepFields = (f: CompanyAnnual): string | null => {
        if (!isManualTaxRep(f)) return null;
        if (!isCompleteTaxRepPhone(f.taxRepTel)
            || !(f.taxRepAddress || '').trim()
            || !(f.taxRepBrNumber || '').trim()
            || !validateTaxRepBr(f.taxRepBrNumber || '')) {
            return 'Please fill in Tax Rep Telephone, Address and BR Number.';
        }
        return null;
    };

    /**
     * Freehand Tax Rep only: upsert admin_tax_rep by name and bind taxRepId.
     * Selecting Certificate / existing Tax Rep list item must not INSERT (design 1.4).
     */
    const resolveTaxRepUpsert = async (f: CompanyAnnual): Promise<CompanyAnnual> => {
        const freehand = Number(f.taxRepSameAsAuditor) === 0
            && !f.taxRepCertificateId
            && !f.taxRepId
            && Boolean((f.taxRepName || '').trim());
        if (!freehand) return f;
        const taxRepName = (f.taxRepName || '').trim();
        const saved = await TaxRepService.saveOrUpdateByName({
            taxRepName,
            telephone: normalizeTaxRepPhone(f.taxRepTel).trim(),
            // 与 Tax Rep 展示名对齐，清掉主档里无关的 herebyAuthorize 脏数据
            herebyAuthorize: taxRepName,
            address: (f.taxRepAddress || '').trim(),
            brNumber: (f.taxRepBrNumber || '').trim(),
        });
        return {
            ...f,
            taxRepId: saved?.id ?? f.taxRepId,
            taxRepSameAsAuditor: 0,
            taxRepCertificateId: null,
            taxRepTel: normalizeTaxRepPhone(f.taxRepTel),
            taxRepHerebyAuthorize: taxRepName,
        };
    };

    /** Auto-correct period dates to dd/MM/yyyy; return null if any non-empty date is unparseable. */
    const normalizeFormDates = useCallback((src: CompanyAnnual): CompanyAnnual | null => {
        const next: CompanyAnnual = {...src};
        const keys: Array<keyof CompanyAnnual> = [
            'periodFrom', 'periodTo', 'lastYearPeriodFrom', 'lastYearPeriodTo', 'changeEffectiveDate', 'commenceDate',
        ];
        for (const key of keys) {
            const raw = next[key];
            if (raw == null || String(raw).trim() === '') continue;
            if (isStrictDdMmYyyy(String(raw))) continue;
            const fixed = normalizeToDdMmYyyy(String(raw));
            if (!fixed) return null;
            (next as any)[key] = fixed;
        }
        return next;
    }, []);

    // Validation helper
    const validateRequiredFields = (workingForm?: CompanyAnnual | null) => {
        const f = workingForm ?? form;
        const errors: typeof validationErrors = {};

        if (!f?.periodFrom) {
            errors.periodFrom = 'Period From is required';
        } else if (!isStrictDdMmYyyy(f.periodFrom)) {
            errors.periodFrom = DATE_FORMAT_HINT;
        }
        if (!f?.periodTo) {
            errors.periodTo = 'Period To is required';
        } else if (!isStrictDdMmYyyy(f.periodTo)) {
            errors.periodTo = DATE_FORMAT_HINT;
        }
        if (f?.lastYearPeriodFrom && !isStrictDdMmYyyy(f.lastYearPeriodFrom)) {
            errors.periodFrom = errors.periodFrom || DATE_FORMAT_HINT;
        }
        if (f?.lastYearPeriodTo && !isStrictDdMmYyyy(f.lastYearPeriodTo)) {
            errors.periodTo = errors.periodTo || DATE_FORMAT_HINT;
        }
        if (!f?.taxFileNo) {
            errors.taxFileNo = 'Tax File No is required';
        }
        if (f) {
            const taxRepMsg = validateTaxRepFields(f);
            if (taxRepMsg) {
                errors.taxRep = taxRepMsg;
            }
        }

        setValidationErrors(errors);
        const valid = Object.keys(errors).length === 0;
        if (!valid) {
            lastValidationMessageRef.current = formatAnnualValidationMessage(errors);
            if (errors.taxFileNo) {
                scrollToAnnualField('taxFileNo');
            } else if (errors.periodFrom || errors.periodTo) {
                scrollToAnnualField('period');
            } else if (errors.taxRep) {
                scrollToAnnualField('taxRep');
            }
        } else {
            lastValidationMessageRef.current = null;
        }
        return valid;
    };

    const onCreate = async (): Promise<boolean> => {
        if (!form) return false;

        const normalized = normalizeFormDates(form);
        if (!normalized) {
            setSnackbar({
                open: true,
                message: DATE_FORMAT_HINT,
                severity: 'error',
            });
            scrollToAnnualField('period');
            validateRequiredFields(form);
            return false;
        }
        if (normalized !== form) {
            setForm(normalized);
        }

        // Validate required fields
        if (!validateRequiredFields(normalized)) {
            const message = lastValidationMessageRef.current || 'Please fill in all required fields';
            setSnackbar({
                open: true,
                message,
                severity: 'error'
            });
            return false;
        }

        if (periodConfirmValidateRef.current && !periodConfirmValidateRef.current()) {
            scrollToAnnualField('period');
            return false;
        }

        if (isFirstAuditBlocking) {
            setSnackbar({
                open: true,
                message: firstAuditBlockingMessage,
                severity: 'error',
            });
            return false;
        }

        if (Number(normalized.isChangeName) === 1) {
            const hasNewEn = Boolean((normalized.companyName || '').trim());
            const hasNewZh = Boolean((normalized.companyChineseName || '').trim());
            if (!hasNewEn && !hasNewZh) {
                setSnackbar({
                    open: true,
                    message: 'Please enter the new Company Name or Company Chinese Name.',
                    severity: 'error',
                });
                return false;
            }
            if (!(normalized.changeEffectiveDate || '').trim()) {
                setSnackbar({
                    open: true,
                    message: 'Please fill in "Since when?" (change effective date) when Change of Co. name is Yes.',
                    severity: 'error',
                });
                return false;
            }
        }

        // Child panels manage their own edits; no flush needed
        // sanitize auditStaffs on create as well (source is userId)
        let working = normalized;
        try {
            working = await resolveTaxRepUpsert(normalized);
            if (working !== normalized) {
                setForm(working);
            }
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Failed to save Tax Rep', severity: 'error'});
            return false;
        }
        const seen = new Set<number>();
        const cleanAuditStaffs = (working.auditStaffs || [])
            .filter(s => s && Number(s.userId || 0) > 0)
            .filter(s => {
                const id = Number(s.userId);
                if (seen.has(id)) return false;
                seen.add(id);
                return true;
            })
            .map(s => ({userId: Number(s.userId), roleType: Number(s.roleType || 0)}));
            const isChangeNameYes = Number(working.isChangeName) === 1;
            const payload: CompanyAnnualPayload = {
                ...working,
                functionalCurrency: working.functionalCurrency ?? 'HKD',
                liabilitiesCurrency: working.liabilitiesCurrency ?? 'HKD',
                section88: Number(working.section88) === 1 ? 1 : 0,
                isChangeName: isChangeNameYes ? 1 : Number(working.isChangeName) === 0 ? 0 : working.isChangeName,
                // When No: ensure change fields cleared before save (design 2.2.1)
                ...(isChangeNameYes
                    ? {
                        oldCompanyName: (working.oldCompanyName || '').trim()
                            || (working.currentCompanyName || '').trim()
                            || null,
                        oldCompanyChineseName: (working.oldCompanyChineseName || '').trim()
                            || (working.currentCompanyChineseName || '').trim()
                            || null,
                        changeEffectiveDate: (working.changeEffectiveDate || '').trim() || null,
                    }
                    : Number(working.isChangeName) === 0
                        ? {
                            changeEffectiveDate: null,
                            oldCompanyName: null,
                            oldCompanyChineseName: null,
                        }
                        : {}),
                auditStaffs: cleanAuditStaffs
            };
        try {
            setLoading(true);
            if (createGuardFlow) {
                const blockedMessage = await validateCompanyAnnualFormCreateFlow({
                    form: payload as CompanyAnnual,
                    expectedFlow: createGuardFlow,
                });
                if (blockedMessage) {
                    setSnackbar({open: true, message: blockedMessage, severity: 'error'});
                    return false;
                }
            }
            const res = await createAnnual(payload);
            const name = (res as any)?.companyName || '';
            const newAnnualId: number | undefined = (res as any)?.annualId;
            const newCompanyId: number | undefined = (res as any)?.companyId;
            if (newAnnualId && newCompanyId && createFlowType) {
                try {
                    await AuditFsAnnualService.initFsStatus(newCompanyId, newAnnualId, createFlowType);
                } catch (_) {
                }
            }
            onClose({message: `${name || 'Company Annual'} Create Successful!`, severity: 'success'});
            return true;
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Create failed!', severity: 'error'});
            return false;
        } finally {
            setLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        submit: async () => {
            if (isAdd) return onCreate();
            return onSave();
        },
        getValidationMessage: () => lastValidationMessageRef.current,
    }));

    const renderContent = () => (
        loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                <CircularProgress color="secondary"/>
            </Box>
        ) : error ? (
            <Typography color="error">{error}</Typography>
        ) : !form ? (
            <Typography color="text.secondary">No data</Typography>
        ) : (
            <Box sx={{pt: 1, position: 'relative'}}>
                <FirstAuditPeriodConfirmDialog
                    open={firstAuditDialogOpen}
                    onConfirm={handleFirstAuditConfirm}
                />
                <Box id="annual-detail-field-period">
                <EpPeriod
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    inCorpDate={inCorpDate}
                    confirmValidateRef={periodConfirmValidateRef}
                    initialPeriodData={initialPeriodData}
                />
                </Box>
                <EpBankConfirmations
                    annualId={Number(annualId || 0)}
                    companyName={form?.companyName || undefined}
                    periodFrom={form?.periodFrom || null}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    hideDownload={true}
                />
                <EpCompanyDetails
                    annualId={Number(annualId || 0)}
                    companyId={companyId}
                    onChange={(patch) => {
                        setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev);
                        if (patch.taxFileNo) {
                            setValidationErrors(prev => ({...prev, taxFileNo: undefined}));
                        }
                        // Capture inCorpDate when it's passed from CompanyDetailsExpansionPanel
                        // inCorpDate is not part of CompanyAnnual interface, but passed temporarily
                        const patchWithInCorpDate = patch as any;
                        if (patchWithInCorpDate.inCorpDate !== undefined) {
                            setInCorpDate(patchWithInCorpDate.inCorpDate as string | null);
                        }
                    }}
                    taxFileNoError={validationErrors.taxFileNo}
                    initialData={initialData ? {
                        limitByGuarantee: form?.limitByGuarantee ?? initialData.limitByGuarantee,
                        limitByGuaranteeLocked: form?.limitByGuaranteeLocked ?? initialData.limitByGuaranteeLocked,
                        changeOfAddress: form?.changeOfAddress ?? initialData.changeOfAddress,
                        lastAddress: form?.lastAddress ?? initialData.lastAddress,
                        // 只用 Excel/导入种子一次，禁止把 form.taxFileNo 回灌成 initialData（否则打字会被旧值冲掉）
                        taxFileNo: initialData.taxFileNo ?? null,
                    } : undefined}
                />
                <EpShareholding
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                />
                <EpAllotmentOfShares
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    initialData={initialData ? {
                        allotmentOfShares: form?.allotmentOfShares,
                        noOfShares: form?.noOfShares,
                        pricePerShare: form?.pricePerShare,
                        priceCurrency: form?.priceCurrency,
                    } : undefined}
                />
                <EpHoldingStructure
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    initialData={initialData ? {
                        holdingCo: form?.holdingCo,
                        nameOfImmediate: form?.nameOfImmediate,
                        nameOfUltimate: form?.nameOfUltimate,
                    } : undefined}
                />
                <EpAuditOpinion
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    forceFirstAudit={isFirstAuditForced}
                    initialData={initialData ? {
                        qualifyOpinion: form?.qualifyOpinion,
                        firstAudit: form?.firstAudit,
                    } : undefined}
                />
                <EpOtherDisclosures
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    initialData={initialData ? {
                        ppe: form?.ppe,
                        relatedPartyTransactions: form?.relatedPartyTransactions,
                        rentalCommitment: form?.rentalCommitment,
                    } : undefined}
                />
                <EpDividend
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    initialData={initialData ? {
                        dividendPaid: form?.dividendPaid,
                        dividendCurrency: form?.dividendCurrency,
                        amountPerEachShare: form?.amountPerEachShare,
                        totalDividendAmount: form?.totalDividendAmount,
                    } : undefined}
                />
                <EpCompanyNameAuditorChanges
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    hideFormerAuditor={form?.qualifyOpinion === 1}
                    initialData={form ? {
                        isChangeName: form.isChangeName,
                        companyName: form.companyName,
                        companyChineseName: form.companyChineseName,
                        currentCompanyName: form.currentCompanyName,
                        currentCompanyChineseName: form.currentCompanyChineseName,
                        oldCompanyName: form.oldCompanyName,
                        oldCompanyChineseName: form.oldCompanyChineseName,
                        changeEffectiveDate: form.changeEffectiveDate,
                        changeOfFinancialYearEnd: form.changeOfFinancialYearEnd,
                        dateOfBoardResolution: form.dateOfBoardResolution,
                        reasonToChange: form.reasonToChange,
                        formerAuditor: form.formerAuditor,
                        addressOfFormerAuditor: sanitizeFormerAuditorAddress(form.formerAuditor, form.addressOfFormerAuditor),
                        professionalClearance: form.professionalClearance,
                    } : undefined}
                />
                <EpBusiness
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                />
                <EpCurrencyExchange
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    initialData={initialData ? {
                        presentationCurrency: form?.presentationCurrency,
                        functionalCurrency: form?.functionalCurrency,
                    } : undefined}
                />
                <EpSmallCashConfirmation
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                />
                <EpTax
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    initialData={initialData ? {
                        twoTierTaxThisYear: form?.twoTierTaxThisYear,
                    } : undefined}
                />
                <EpAuditStaffs
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                />
                <Box id="annual-detail-field-taxRep">
                <EpAuditor
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                    externalErrors={{taxRep: Boolean(validationErrors.taxRep)}}
                />
                </Box>
                <EpOther
                    annualId={Number(annualId || 0)}
                    onChange={(patch) => setForm(prev => prev ? ({...prev, ...patch}) as CompanyAnnual : prev)}
                />
            </Box>
        )
    );

    if (mode === 'content') {
        return (
            <Box sx={{bgcolor: theme.palette.background.default, borderRadius: 1}}>
                {!hideActions && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2, py: 1 }}>
                        <Button variant="outlined" color="inherit" onClick={() => onClose()} disabled={loading}>
                            Cancel
                        </Button>
                    </Box>
                )}
                <DialogContent dividers sx={{bgcolor: theme.palette.background.default, pt: 0}}>
                    {renderContent()}
                </DialogContent>
                {!hideActions && (
                    <DialogActions>
                        {isAdd ? (
                            <Button variant="contained" color="secondary" onClick={onCreate} disabled={loading}>
                                Create
                            </Button>
                        ) : (
                            <Button variant="contained" color="secondary" onClick={onSave} disabled={loading}>
                                Save
                            </Button>
                        )}
                    </DialogActions>
                )}
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message || ''}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({open: false})}
                />
            </Box>
        );
    }

    if (inline) {
        return (
            <Box sx={{
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Box component="span" sx={{fontWeight: 700}}>Annual Detail</Box>
                    <IconButton aria-label="close" onClick={() => onClose()} sx={{ml: 'auto'}}>
                        <CloseIcon/>
                    </IconButton>
                </Box>
                <DialogContent dividers sx={{bgcolor: theme.palette.background.default}}>
                    {renderContent()}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="inherit" onClick={() => onClose()} disabled={loading}>
                        Cancel
                    </Button>
                    {isAdd ? (
                        <Button variant="contained" color="secondary" onClick={onCreate} disabled={loading}>
                            Create
                        </Button>
                    ) : (
                        <Button variant="contained" color="secondary" onClick={onSave} disabled={loading}>
                            Save
                        </Button>
                    )}
                </DialogActions>
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message || ''}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({...s, open: false}))}
                />
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                onClose();
            }}
            fullWidth
            maxWidth="md"
            sx={{'& .MuiDialog-paper': {bgcolor: theme.palette.background.paper, boxShadow: theme.shadows[3]}}}
            disableEscapeKeyDown
        >
            <DialogTitle>
                Annual Detail
                <IconButton aria-label="close" onClick={() => onClose()} sx={{position: 'absolute', right: 8, top: 8}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{bgcolor: theme.palette.background.default}}>
                {renderContent()}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="inherit" onClick={() => onClose()}
                        disabled={loading}>
                    Cancel
                </Button>
                {isAdd ? (
                    <Button variant="contained" color="secondary" onClick={onCreate} disabled={loading}>
                        Create
                    </Button>
                ) : (
                    <Button variant="contained" color="secondary" onClick={onSave} disabled={loading}>
                        Save
                    </Button>
                )}
            </DialogActions>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message || ''}
                severity={snackbar.severity}
                onClose={() => setSnackbar(s => ({...s, open: false}))}
            />
        </Dialog>
    );
});

export default CompanyAnnualDetail;
